import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiCall } from '../../lib/api.js';
import { useAccessToken, requireToken } from '../../lib/use-access-token.js';

export type MemberRole = 'company_admin' | 'company_hr' | 'company_viewer';

export interface MemberListItem {
  userId: string;
  email: string;
  displayName: string;
  entraUpn: string;
  roles: MemberRole[];
  active: boolean;
}

export function useMembers(companyId: string | undefined) {
  const { token } = useAccessToken();
  return useQuery<{ items: MemberListItem[] }, Error>({
    queryKey: ['members', companyId, token],
    enabled: !!companyId && !!token,
    queryFn: async ({ signal }) =>
      apiCall(`/v1/companies/${companyId}/members`, { token: requireToken(token), signal }),
    staleTime: 15_000,
    retry: false,
  });
}

export function useAddMember(companyId: string | undefined) {
  const { token } = useAccessToken();
  const qc = useQueryClient();
  return useMutation<
    { userId: string; role: MemberRole },
    Error,
    { email: string; role: MemberRole }
  >({
    mutationFn: async (input) =>
      apiCall(`/v1/companies/${companyId}/members`, {
        method: 'POST',
        token: requireToken(token),
        body: input,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['members', companyId] });
    },
  });
}

export function useRemoveMember(companyId: string | undefined) {
  const { token } = useAccessToken();
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string; role: MemberRole }>({
    mutationFn: async (input) =>
      apiCall(`/v1/companies/${companyId}/members/${input.userId}/${input.role}`, {
        method: 'DELETE',
        token: requireToken(token),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['members', companyId] });
    },
  });
}
